import { Button } from "design-system/atoms/Button"
import { Textarea } from "design-system/atoms/Textarea"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import { cn } from "design-system/utils"
import { tryCatchFallback } from "lopermut"
import { Pencil, Plus } from "lucide-react"
import { useState } from "react"
import { Trans, useTranslation } from "react-i18next"

type Props = {
  metadata: Record<string, unknown> | null | string | number | boolean
  onChange: (value: string) => void
  onBlur: () => void
  className?: string
}

export function Metadata({ metadata, onChange, onBlur, className }: Props) {
  const { t } = useTranslation()
  const [isEditing, setIsEditing] = useState(false)

  const formattedMetadata = formatMetadata(metadata)
  const hasMetadata =
    metadata !== null &&
    metadata !== "" &&
    (typeof metadata !== "object" || Object.keys(metadata).length > 0)

  const title = (
    <Tooltip>
      <TooltipTrigger asChild slick>
        <span>{t("customers.profile.metadata")}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm">
        <Trans
          i18nKey="customers.profile.metadataDescription"
          components={{
            a: (
              <a
                href="https://www.json.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground underline hover:text-muted-foreground/80"
              />
            ),
          }}
        />
      </TooltipContent>
    </Tooltip>
  )

  function renderValue() {
    if (isEditing) {
      return (
        <Textarea
          autoFocus
          placeholder={t("customers.profile.metadataPlaceholder")}
          className="font-mono"
          value={formattedMetadata}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => {
            onBlur()
            if (isValidMetadata(metadata)) setIsEditing(false)
          }}
        />
      )
    }

    const editButton = (
      <Button
        type="button"
        variant="link"
        size="icon"
        className="h-6 w-6 bg-transparent"
        onClick={() => setIsEditing(true)}
      >
        <Pencil className="h-4 w-4" />
      </Button>
    )

    if (!hasMetadata) {
      return (
        <div>
          <Button type="button" variant="secondary" size="sm" onClick={() => setIsEditing(true)}>
            <Plus />
            <span>{t("customers.profile.addMetadata")}</span>
          </Button>
        </div>
      )
    }

    return (
      <MarkdownRenderer expandLevel={1} jsonActions={editButton}>
        {`\`\`\`json\n${formattedMetadata}\n\`\`\``}
      </MarkdownRenderer>
    )
  }

  return (
    <div className={cn("grid grid-cols-[9rem_minmax(0,1fr)] items-start gap-3", className)}>
      <div className="flex text-sm text-muted-foreground leading-8">{title}</div>
      <div className="flex min-w-0 flex-col">{renderValue()}</div>
    </div>
  )
}

function isValidMetadata(metadata: Props["metadata"]) {
  if (typeof metadata !== "string" || metadata === "") return true
  return tryCatchFallback(() => {
    JSON.parse(metadata)
    return true
  }, false)
}

function formatMetadata(metadata: Props["metadata"]) {
  if (metadata === null) return ""
  if (typeof metadata === "object") return JSON.stringify(metadata, null, 2)
  return String(metadata)
}
